// frontend/src/components/chat/MessageInput.js
// Input para enviar mensajes - Con archivos y audio

import React, { useState, useRef, useEffect } from 'react';
import './MessageInput.css';

const MessageInput = ({ onSend, conversationId, replyTo, onCancelReply }) => {
    const [message, setMessage] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);

    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingTimerRef = useRef(null);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
        }
    }, [message]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
        };
    }, []);

    // Manejar envio
    const handleSend = () => {
        if (selectedFile) {
            onSend({
                file: selectedFile,
                replyToId: replyTo?.id
            });
            clearFile();
        } else if (message.trim()) {
            onSend({
                content: message.trim(),
                replyToId: replyTo?.id
            });
            setMessage('');
        }
        onCancelReply?.();
    };

    // Manejar tecla Enter
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Seleccionar archivo
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const maxSize = file.type.startsWith('audio/') ? 5 : 10;
        if (file.size > maxSize * 1024 * 1024) {
            alert(`El archivo no puede exceder ${maxSize}MB`);
            return;
        }

        setSelectedFile(file);

        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => setFilePreview(e.target.result);
            reader.readAsDataURL(file);
        } else {
            setFilePreview(null);
        }
    };

    // Limpiar archivo
    const clearFile = () => {
        setSelectedFile(null);
        setFilePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Iniciar grabacion de audio
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (e) => {
                audioChunksRef.current.push(e.data);
            };

            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const audioFile = new File([audioBlob], `audio-${Date.now()}.webm`, { type: 'audio/webm' });

                onSend({
                    file: audioFile,
                    replyToId: replyTo?.id
                });
                onCancelReply?.();

                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setRecordingTime(0);

            recordingTimerRef.current = setInterval(() => {
                setRecordingTime(t => {
                    if (t >= 120) {
                        stopRecording();
                        return t;
                    }
                    return t + 1;
                });
            }, 1000);
        } catch (error) {
            console.error('Error al iniciar grabacion:', error);
            alert('No se pudo acceder al microfono');
        }
    };

    // Detener grabacion
    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
        }
    };

    // Cancelar grabacion
    const cancelRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            audioChunksRef.current = [];
            setIsRecording(false);
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
        }
    };

    // Formatear tiempo de grabacion
    const formatRecordingTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Emojis basicos
    const quickEmojis = ['😀', '😂', '❤️', '👍', '👋', '🎉', '🔥', '✨'];

    return (
        <div className="urbanq-message-input">
            {/* Reply preview */}
            {replyTo && (
                <div className="urbanq-input-reply">
                    <div className="urbanq-input-reply-content">
                        <span className="urbanq-input-reply-label">
                            Respondiendo a <strong>{replyTo.sender_name}</strong>
                        </span>
                        <span className="urbanq-input-reply-text">{replyTo.content}</span>
                    </div>
                    <button
                        className="urbanq-input-reply-close"
                        onClick={onCancelReply}
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* File preview */}
            {selectedFile && (
                <div className="urbanq-input-file-preview">
                    {filePreview ? (
                        <img src={filePreview} alt="Preview" />
                    ) : (
                        <div className="urbanq-input-file-info">
                            <span className="urbanq-input-file-icon">📄</span>
                            <span className="urbanq-input-file-name">{selectedFile.name}</span>
                        </div>
                    )}
                    <button
                        className="urbanq-input-file-remove"
                        onClick={clearFile}
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Recording UI */}
            {isRecording ? (
                <div className="urbanq-input-recording">
                    <div className="urbanq-recording-indicator">
                        <span className="urbanq-recording-dot"></span>
                        <span className="urbanq-recording-time">{formatRecordingTime(recordingTime)}</span>
                    </div>
                    <div className="urbanq-recording-actions">
                        <button
                            className="urbanq-recording-cancel"
                            onClick={cancelRecording}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                            </svg>
                        </button>
                        <button
                            className="urbanq-recording-send"
                            onClick={stopRecording}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                            </svg>
                        </button>
                    </div>
                </div>
            ) : (
                <div className="urbanq-input-main">
                    {/* Acciones izquierda */}
                    <div className="urbanq-input-actions">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,audio/*"
                            style={{ display: 'none' }}
                        />
                        <button
                            className="urbanq-input-btn"
                            onClick={() => fileInputRef.current?.click()}
                            title="Adjuntar archivo"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
                            </svg>
                        </button>
                    </div>

                    {/* Textarea */}
                    <textarea
                        ref={textareaRef}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Escribe un mensaje..."
                        rows={1}
                        className="urbanq-input-textarea"
                        disabled={!!selectedFile}
                    />

                    {/* Acciones derecha */}
                    <div className="urbanq-input-actions">
                        {/* Emoji picker toggle */}
                        <div className="urbanq-emoji-container">
                            <button
                                className="urbanq-input-btn"
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                title="Emojis"
                            >
                                😊
                            </button>
                            {showEmojiPicker && (
                                <div className="urbanq-emoji-picker">
                                    {quickEmojis.map(emoji => (
                                        <button
                                            key={emoji}
                                            onClick={() => {
                                                setMessage(m => m + emoji);
                                                setShowEmojiPicker(false);
                                            }}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Grabar audio */}
                        <button
                            className="urbanq-input-btn urbanq-mic-btn"
                            onClick={startRecording}
                            title="Grabar audio"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                            </svg>
                        </button>

                        {/* Botón enviar - siempre visible */}
                        <button
                            className={`urbanq-input-send ${!(message.trim() || selectedFile) ? 'disabled' : ''}`}
                            onClick={handleSend}
                            disabled={!(message.trim() || selectedFile)}
                            title="Enviar mensaje"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MessageInput;
